import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomAutocompleteMulti from "../../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { VerticalAlignGrid } from "../../../invoices/styled";
import { StyledFormSection } from "../styles";
import LabelSelectRow from "./LabelSelectRow";
import { formDateValidation } from "../../../../app/common";
import { useContractDataContext } from "../ContractDataProvider";

const RADIO_ALL_SELECTION = "all";
const RADIO_RESTRICTED_SELECTION = "restricted";
const MAIN_FORM_CUSTOMERS_USING_CONTRACT_KEY = "customerOrgId";

type RadioButtonSelection =
  | typeof RADIO_ALL_SELECTION
  | typeof RADIO_RESTRICTED_SELECTION;

type OrganizationSelection = { id: string; name: string };

const ContractPartiesSection = () => {
  const { t } = useTranslation("customers");
  const {
    allCustomerOrganizations,
    representativeCustomerOrganization,
    subCustomerOrganizations,
  } = useContractDataContext();
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const allCustomerOrganizationsOptions = allCustomerOrganizations.map(
    (org) => ({
      id: org.id,
      name: org.name,
    }),
  ) as OrganizationSelection[];
  const representativeCustomerOrganizationOption = {
    id: representativeCustomerOrganization?.id || "",
    name: representativeCustomerOrganization?.name || "",
  } as OrganizationSelection;
  const customerOrganizationOptions = subCustomerOrganizations.map((org) => ({
    id: org.id,
    name: org.name,
  })) as OrganizationSelection[];
  const organizationsUsingContractExistingValue = getValues(
    MAIN_FORM_CUSTOMERS_USING_CONTRACT_KEY,
  );

  const organizationsUsingContract =
    organizationsUsingContractExistingValue.filter(
      (org: { id: string; name: string }) =>
        org.id !== representativeCustomerOrganization?.id,
    );

  const isAllOrgsSelected =
    allCustomerOrganizations.length ===
    organizationsUsingContractExistingValue.length;
  const radioButtonInitialValue = isAllOrgsSelected
    ? RADIO_ALL_SELECTION
    : RADIO_RESTRICTED_SELECTION;
  const localOrgsUsingContractInitialValue = isAllOrgsSelected
    ? []
    : organizationsUsingContract;
  const [radioButtonSelection, setRadioButtonSelection] =
    useState<RadioButtonSelection>(radioButtonInitialValue);
  const [localOrganizationsUsingContract, setLocalOrganizationsUsingContract] =
    useState<OrganizationSelection[]>(localOrgsUsingContractInitialValue);

  const handleMainFormDataChange = (newData: OrganizationSelection[]) =>
    setValue(MAIN_FORM_CUSTOMERS_USING_CONTRACT_KEY, newData, {
      shouldDirty: true,
    });

  const handleRadioButtonChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newSelection = event.target.value as RadioButtonSelection;
    handleMainFormDataChange(
      newSelection === RADIO_ALL_SELECTION
        ? allCustomerOrganizationsOptions
        : [
            representativeCustomerOrganizationOption,
            ...localOrganizationsUsingContract,
          ],
    );
    setRadioButtonSelection(newSelection);
  };

  const handleSelectionChange = (newSelections: OrganizationSelection[]) => {
    handleMainFormDataChange([
      representativeCustomerOrganizationOption,
      ...newSelections,
    ]);
    setLocalOrganizationsUsingContract(newSelections);
  };

  const showCustomersUsingContractSection =
    customerOrganizationOptions.length >= 2;

  return (
    <StyledFormSection title={t("contract.partiesOfTheContract")}>
      <LabelSelectRow
        label={t("contract.clientCompany")}
        fieldName="customerCompanyName"
        options={[getValues("customerCompanyName")]}
        showTooltip={showCustomersUsingContractSection}
        tooltipText={t("contract.clientCompanyTooltip")}
        disabled
      />
      <LabelSelectRow
        label={t("contract.eezyCompany")}
        fieldName="eezyCompanyName"
        options={[getValues("eezyCompanyName")]}
        disabled
      />
      <VerticalAlignGrid container spacing={1}>
        <Grid
          container
          item
          xs={3}
          justifyContent="flex-end"
          paddingRight="0.5rem"
        >
          <Typography variant="secondary">
            {t("settings:effectiveFrom")}
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Controller
            name="validFromDate"
            control={control}
            rules={{
              validate: (date) =>
                formDateValidation(date, t("common:invalidDate")),
            }}
            render={({ field }) => (
              <DesktopDatePicker
                {...field}
                value={field.value}
                format="dd.MM.yyyy"
                onChange={(newDate: Date | null) => field.onChange(newDate)}
                slots={{ textField: TextFieldLight }}
                slotProps={{
                  textField: {
                    error: !!errors.validFromDate,
                    helperText: errors.validFromDate?.message as string,
                    FormHelperTextProps: {
                      sx: {
                        position: "absolute",
                        top: "2.3rem",
                        width: "250px",
                      },
                    },
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            )}
          />
        </Grid>
      </VerticalAlignGrid>
      {showCustomersUsingContractSection && (
        <Grid container spacing={1}>
          <Grid
            container
            item
            xs={3}
            justifyContent="flex-end"
            paddingRight="0.5rem"
          >
            <Typography
              sx={{ marginTop: "0.5rem", textAlign: "right" }}
              variant="secondary"
            >
              {t("contract.customersUsingContract")}
            </Typography>
          </Grid>
          <Grid item xs={7} marginLeft="1rem">
            <RadioGroup
              value={radioButtonSelection}
              onChange={handleRadioButtonChange}
            >
              <FormControlLabel
                sx={{ width: "fit-content" }}
                checked={radioButtonSelection === RADIO_ALL_SELECTION}
                label={t("common:all")}
                value="all"
                control={<Radio />}
              />
              <Typography
                sx={{ marginLeft: "2rem", fontSize: "12px" }}
                variant="secondary"
              >
                {t("contract.customersUsingContractAllGuideText")}
              </Typography>
              <Box display="flex">
                <FormControlLabel
                  checked={radioButtonSelection === RADIO_RESTRICTED_SELECTION}
                  sx={{ minWidth: "119px" }}
                  label={t("workLocationNS.selectedOnly")}
                  value={RADIO_RESTRICTED_SELECTION}
                  control={<Radio />}
                />
                <Box display="flex" alignItems="center" width="100%">
                  <Controller
                    name={MAIN_FORM_CUSTOMERS_USING_CONTRACT_KEY}
                    control={control}
                    render={({ field: { value } }) => (
                      <CustomAutocompleteMulti
                        disabled={radioButtonSelection === RADIO_ALL_SELECTION}
                        resetValues={
                          radioButtonSelection === RADIO_ALL_SELECTION
                        }
                        options={customerOrganizationOptions}
                        customInputLabel={t("contract.searchCustomers")}
                        customInputLabelProps={{ shrink: undefined }}
                        defaultValue={localOrganizationsUsingContract}
                        fullWidth
                        name={MAIN_FORM_CUSTOMERS_USING_CONTRACT_KEY}
                        onChange={(_: any, newValue: AutocompleteType[]) => {
                          const formattedValue = newValue.map((v) => ({
                            id: v.id,
                            name: v.name,
                          }));
                          handleSelectionChange(formattedValue);
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
              <Typography
                sx={{ marginLeft: "2rem", fontSize: "12px" }}
                variant="secondary"
              >
                {t("contract.customersUsingContractSelectedOnlyGuideText")}
              </Typography>
            </RadioGroup>
          </Grid>
        </Grid>
      )}
    </StyledFormSection>
  );
};

export default ContractPartiesSection;
